import { Check } from "lucide-react";
import { cn } from "@/utils/utils";
import { SubmissionTypeBadge } from "@/ui/atoms";
import { formatHomepageRowCopy } from "@/utils/formatHomepageRowCopy";
import {
    homepageColumnTint,
    homepageRowTimeliness,
    homepageTimelinessClass,
} from "@/utils/homepageColours";
import type { THomepageColumnKey, THomepageRow, THomepageView } from "@/store";

export interface TSubmissionRowProps {
    row: THomepageRow;
    view: THomepageView;
    /** Which column this row is rendered in — drives copy (design §7.1) and
     * the leading indicator (§5.5). */
    columnKey: THomepageColumnKey;
    onClick?: () => void;
    className?: string;
}

/**
 * One board row (design doc §7.1, §5.5). All copy comes from
 * `formatHomepageRowCopy` and all colour from `homepageColours` — this
 * component does no date maths, label mapping or palette decisions of its own.
 *
 * One indicator, derived rather than passed in: a leading check on a submitted
 * row (green on time, orange late). Problem rows — failed uploads, errors to
 * fix, an overdue deadline — carry no icon and no red text: most submissions
 * have something to fix, so flagging each one in red drowns the board.
 */
export function SubmissionRow({ row, view, columnKey, onClick, className }: TSubmissionRowProps) {
    const title =
        view === "service_provider"
            ? (row.portfolioCompanyName ?? row.dealName)
            : (row.serviceProviderName ?? row.dealName);
    const subtitle = view === "accountant" ? row.portfolioCompanyName : null;
    const copy = formatHomepageRowCopy(row, columnKey);
    const timeliness = homepageRowTimeliness(row, columnKey);
    // Only a satisfied row is badged. An overdue one used to carry a clock;
    // the copy already says it is late, and the icon only added noise.
    const showCheck = timeliness === "on_time" || timeliness === "late";
    const timelinessLabel = timeliness === "on_time" ? "Submitted on time" : "Submitted late";

    const isInteractive = row.isClickable && Boolean(onClick);
    const tint = homepageColumnTint(columnKey);

    return (
        <div
            // Identified by the tuple the board is keyed on, so a journey can
            // target one submission without depending on card text.
            data-testid="submission-row"
            data-deal-id={row.dealId}
            data-period={row.period}
            data-file-type={row.fileType}
            role={isInteractive ? "button" : undefined}
            tabIndex={isInteractive ? 0 : undefined}
            onClick={isInteractive ? onClick : undefined}
            onKeyDown={
                isInteractive
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") onClick?.();
                      }
                    : undefined
            }
            className={cn(
                // The card carries its column's colour (§5.5); the column
                // behind it is white. A clickable one deepens that same tint on
                // hover — enough to read as "this does something" without
                // introducing a second colour.
                "rounded-lg border p-3 transition-colors",
                tint.card,
                isInteractive && "cursor-pointer hover:shadow-proda-sm",
                isInteractive && tint.cardHover,
                className,
            )}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1.5">
                    {showCheck && (
                        <Check
                            aria-label={timelinessLabel}
                            className={cn("h-4 w-4 shrink-0", homepageTimelinessClass(timeliness))}
                        />
                    )}
                    <div className="min-w-0">
                        <div className="truncate text-[15px] font-semibold leading-snug">
                            {title}
                        </div>
                        {subtitle && (
                            <div className="truncate text-[13px] text-muted-foreground">
                                {subtitle}
                            </div>
                        )}
                    </div>
                </div>
                <SubmissionTypeBadge dealFileType={row.dealFileType} fileType={row.fileType} />
            </div>
            {/* 13px rather than 12px, and the status line in full-strength
                text: at the smaller muted size these lines were the least
                readable thing on the board, and they carry what the card is
                actually telling you. */}
            <div className="mt-2 space-y-0.5 text-[13px] leading-normal">
                {copy.line1 && <div className="text-foreground">{copy.line1}</div>}
                {copy.line2 && <div className="text-muted-foreground">{copy.line2}</div>}
            </div>
        </div>
    );
}
